/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useGlobalState } from "../../utilities/hooks/use-global-state";
import { CategoryIds } from "../../models/enumerations/category-ids";
import { SlideType } from "../../models/enumerations/slide-types";
import AssessmentSettings from "../../models/interfaces/assessment-settings";
import { Question } from "../../models/interfaces/questions/question";
import { CalculateMovementScore } from "../../models/interfaces/scores/calculate-movement-score";
import { MovementScore } from "../../models/interfaces/scores/movement-score";
import { MskScore } from "../../models/interfaces/scores/msk-score";
import { AssessmentUtils } from "../../utilities/assessments/assessment-utils";
import CalculateMovementService from "../../utilities/services/calculate/calculate-movement-service";
import UserUtilAssessments from "../../utilities/user-util-assessments";
import LoaderAssessments from "../loader-assessments/loader-assessments";
import ScreenSlide from "../screen-slide/screen-slide";
import BaseCardLayout from "../base-card-layout/base-card-layout";
import { User } from "../../models/interfaces/user";
import LinkLifestyleScreen from "../link-lifestyle-screen/link-lifestyle-screen";
import { useTranslation } from 'react-i18next';
import { UserInfo } from "../../models/interfaces/scores/user-info";
import { useHistory } from "react-router-dom";
import { useNavigationDirection } from "../navigation-direction-context/navigation-direction-content";
import { useBackController } from "../back-controller/back-controller";
import { createLogger } from "../../utilities/logger";

const log = createLogger("LinkMovementScreen");
const COMPONENT_CLASS = "c-movement-screen";

export interface LinkMovementScreenProps {
    screenSettings: AssessmentSettings;
    setScreenSettings: React.Dispatch<AssessmentSettings>;
    settingsOnBack?: Function;
    user: User;
    linkId: string;
}

const LinkMovementScreen: React.FC<LinkMovementScreenProps> = (props) => {
    const { screenSettings, setScreenSettings } = props;
    const { setGlobalError, setGlobalErrors, setHasMskScore } = useGlobalState();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userInfo, setUserInfo] = useState<UserInfo>();
    const history = useHistory();
    const [movementScore, setMovementScore] = useState<
        MovementScore | undefined | null
    >(undefined);
    const [errors, setErrors] = useState<string[]>([]);
    // const setLastActivityCompletedDate = useStoreAssessments(
    //     (state) => state.setLastActivityCompleted
    // );
    const [isCompleted, setIsCompleted] = useState(false);
    const [settings, setSettings] = useState<AssessmentSettings>({
        completedGroupIds: [],
        currentSlideIndex: 0,
        currentGroupIndex: 0,
        questionResponses: [],
        slideGroups: [],
    });
    const [mskScore, setMskScore] = useState<MskScore>();
    const hasSubmitButton =
        AssessmentUtils.isLastGroup(screenSettings) &&
        AssessmentUtils.isLastSlide(screenSettings);
    const { t } = useTranslation();
    const { setDirection } = useNavigationDirection();
    const { invoke } = useBackController();

    useEffect(() => {
        UserUtilAssessments.getUserInfoForLinks(props.user).then((userInfo) => {
            if (!userInfo) {
                // they don't have bioSex or birthdate. Take them to setup screen
                history.replace("/setup");
            }
            setUserInfo(userInfo);
        });

        AssessmentUtils.fetchQuestions(
            screenSettings,
            CategoryIds.MOVEMENT,
            null,
            props.linkId
        ).then((settings) => {
            setScreenSettings(settings);
            setIsLoading(false);
        }).catch((e) => {
            log.error("Failed to load movement questions for link", {
                exception: e,
                linkId: props.linkId,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasSubmitButton) {
            submit();
        }
    }, [hasSubmitButton])

    const getNextPage = async () => {
        setErrors([]);
        setDirection("forward");
        setScreenSettings(await AssessmentUtils.moveToNextPage(screenSettings, props.linkId));
    };

    const recordAnswer = async (selectedAnswer: string, question: Question, forceAutoSubmit?: boolean) => {
        let settings = AssessmentUtils.recordAnswer(
            screenSettings,
            question,
            selectedAnswer
        );
        if (errors.length !== 0) {
            setErrors([]);
        }

        if (
            slide?.slideType === SlideType.Question &&
            slide.questions != null
        ) {
            let autoSubmit = false;
            slide.questions.forEach((question) => {
                if (AssessmentUtils.autoSubmitQuestion(question)) {
                    autoSubmit = true;
                }
            });
            setDirection("forward")
            if (autoSubmit || forceAutoSubmit) {
                settings = await AssessmentUtils.moveToNextPage(settings, props.linkId);
            }
        }
        setScreenSettings(settings);
    };

    const submit = async (attemptIndex: number = 0) => {
        setIsLoading(true);
        // const queryParams = new URLSearchParams(window.location.search);


        if (!userInfo) {
            return;
        }
        const calcScore: CalculateMovementScore = {
            userInfo: userInfo,
            movementResponses: AssessmentUtils.getAllResponses(screenSettings),
        };
        let newMskScore: MskScore | null = null;
        try {
            newMskScore = props.linkId
                ? await CalculateMovementService.scoreWithLinkToken(props.linkId, calcScore)
                : await CalculateMovementService.score(calcScore)
        } catch (err) {
            if (attemptIndex === 0) {
                setGlobalError("Movement: Calculate", err);
            }
            const retryMs: number[] = [500, 1000, 1500, 3000, 5000];
            if (attemptIndex < retryMs.length) {
                setTimeout(() => {
                    return submit(attemptIndex + 1);
                }, retryMs[attemptIndex]);
                return;
            } else {
                log.error("Failed to calculate movement score after retries (link)", {
                    exception: err,
                    linkId: props.linkId,
                    attempts: attemptIndex + 1,
                });
            }
        }

        if (newMskScore == null) {
            log.error("Movement score calculation returned no result (link)", {
                linkId: props.linkId,
            });
            setErrors([t('assessments.err_mskScore2')]);
            setIsLoading(false);
            return;
        }

        log.info("Movement score calculated for link user", {
            linkId: props.linkId,
            mskScoreId: newMskScore.id,
            percentage: newMskScore.percentage,
        });

        setMskScore(newMskScore)
        setMovementScore(newMskScore.movementScore);
        setErrors(newMskScore.errors);

        log.info("Link movement assessment submitted", {
            linkId: props.linkId,
            mskScoreId: newMskScore.id,
            percentage: newMskScore.percentage,
        });

        setIsCompleted(true);
    };

    // /**
    //  * Record Scores
    //  * @returns True if succeeeded, false if there was an error
    //  */
    // const recordResults = async (newMskScore: MskScore): Promise<boolean> => {
    //     if (
    //         newMskScore.movementScore == null
    //     ) {
    //         return true;
    //     }


    //     const newErrors: GlobalStateError[] = [];

    //     try {
    //         await MskScoreServiceAssessments.addOrUpdateRecentById(
    //             newMskScore,
    //             userId
    //         ).then(() => {
    //             if (userId) {
    //                 MskScoreUtil.getLatest(userId).then((response) => {
    //                     if (response?.lifestyleScore?.percentage != null &&
    //                         response.movementScore?.percentage != null) {
    //                         setHasMskScore(true);
    //                     }
    //                 });
    //             }
    //         });
    //     } catch (err) {
    //         newErrors.push(
    //             GlobalStateUtil.createError("Movement MSK was not saved", err)
    //         );
    //     }
    //     try {
    //         await MovementScoreService.addOrUpdateRecentById(
    //             newMskScore.movementScore!,
    //             state.user.id
    //         );
    //     } catch (err) {
    //         newErrors.push(
    //             GlobalStateUtil.createError("Movement score was not saved", err)
    //         );
    //     }
    //     try {
    //         await CorrectiveExerciseUtils.handleNewMovementScoreById(
    //             newMskScore.movementScore,
    //             state.user.id
    //         );
    //     } catch (err) {
    //         newErrors.push(
    //             GlobalStateUtil.createError(
    //                 "Movement score could not be handled",
    //                 err
    //             )
    //         );
    //     }
    //     try {
    //         await UserUtilAssessments.handleNewMskScore(userId, {
    //             mskScore: newMskScore,
    //             wasMovementRetake: true,
    //         });
    //     } catch (err) {
    //         newErrors.push(
    //             GlobalStateUtil.createError(
    //                 "Movement retake state wasn't updated",
    //                 err
    //             )
    //         );
    //     }
    //     try {
    //         await UserMskFocusArticlesUtil.handleFocusChanges(
    //             globalState.currentIdentity!.id!,
    //             newMskScore
    //         );
    //     } catch (err) {
    //         newErrors.push(
    //             GlobalStateUtil.createError(
    //                 "Movement focus wasn't updated",
    //                 err
    //             )
    //         );
    //     }

    //     if (newErrors.length >= 1) {
    //         // set errors
    //         setErrors(newErrors.map((x) => x.description));

    //         // update global state with errors
    //         setGlobalErrors(...newErrors);
    //         return false;
    //     }
    //     return true;
    // };

    const groups = AssessmentUtils.getAssessmentGroups(screenSettings);
    const currentGroup =
        screenSettings?.slideGroups == null
            ? null
            : screenSettings.slideGroups[screenSettings.currentGroupIndex];
    const slide = AssessmentUtils.getSlide(screenSettings);
    const actionsModifier =
        slide?.slideType === SlideType.Video ? "-video" : "";

    let hasContinueButton =
        AssessmentUtils.hasData(screenSettings) &&
        movementScore == null &&
        (!AssessmentUtils.isLastGroup(screenSettings) ||
            !AssessmentUtils.isLastSlide(screenSettings));

    if (
        hasContinueButton &&
        slide &&
        slide?.slideType === SlideType.Question &&
        slide.questions != null
    ) {
        slide.questions.forEach((question) => {
            if (AssessmentUtils.autoSubmitQuestion(question)) {
                hasContinueButton = false;
            }
            if (AssessmentUtils.multiQuestion(question)) {
                hasContinueButton = false
            }
        });
    }

    let continueButtonText = t('buttons.btn_continue');;
    if (hasContinueButton && slide != null) {
        switch (slide.slideType) {
            case SlideType.AssessmentIntro:
            case SlideType.Tests:
                continueButtonText = t('buttons.btn_letsDoIt');
                break;
            case SlideType.Tips:
                continueButtonText = t('buttons.btn_gotIt');
                break;
            case SlideType.GroupIntro:
                continueButtonText = t('buttons.btn_start');
                break;
            case SlideType.Video:
                if (slide.video) {
                    continueButtonText = t('buttons.btn_enterResults');
                }
                break;
        }
    }

    const noProgressBarSlideTypes = [
        SlideType.AssessmentIntro,
        SlideType.Complete,
        SlideType.GroupIntro,
        SlideType.Tests,
        SlideType.Tips,
    ];
    const hasProgressBar =
        slide == null ||
        noProgressBarSlideTypes.indexOf(slide.slideType) === -1;
    const questionGroupNames = AssessmentUtils.getAssessmentGroups(
        screenSettings
    ).map((group) => group.groupId!);

    // const handleGenerateReport = async (emailResults: boolean) => {
    //     if (!globalState.currentIdentity?.id) return;

    //     // we need to get the user from the firebase in case their organization
    //     // was switched (in the portal) and no longer matches the data in the app.
    //     const userInFirebase = await UserService.get(globalState.currentIdentity?.id);

    //     if (!userInFirebase || !userInFirebase.organizationId) return;

    //     const latestOrgId = userInFirebase.organizationId;

    //     //setIsLoading(true);

    //     const date = DateTime.now().toISODate();
    //     const report: Report = {
    //         dateEnd: date,
    //         dateStart: date,
    //         emailResults: emailResults,
    //         organizationId: latestOrgId,
    //         status: "scheduled",
    //         type: ReportType.Assessment,
    //         userId: globalState.currentIdentity!.id!,
    //     };

    //     const identity = globalState.currentIdentity;

    //     if (identity.organizationId !== latestOrgId) {
    //         identity.organizationId = latestOrgId;
    //     }

    //     const scheduledReport = await ReportService.save(
    //         report,
    //         identity
    //     );

    //     if (!scheduledReport.id) {
    //         // setIsLoading(false);
    //         // setReportError(
    //         //     "Report was not saved successfully. Please try again."
    //         // );
    //         return;
    //     }

    //     if (scheduledReport.emailResults === true) {
    //         // setShowReportEmailConfirmation(true);
    //         // setIsLoading(false);
    //         return;
    //     }

    //     // const reportUnsubscribe = ReportService.getSnapshot(
    //     //     scheduledReport.id,
    //     //     (r: Report) => {
    //     //         if (r.status === "complete" && r.url) {
    //     //             setIsLoading(false);
    //     //             reportUnsubscribe();

    //     //             //Loads in the System browser
    //     //             window.open(r.url, "_self");
    //     //         }
    //     //         if (r.status === "error") {
    //     //             setReportError(r.errorMessage);
    //     //             setIsLoading(false);
    //     //             reportUnsubscribe();
    //     //         }
    //     //     }
    //     // );
    // };

    const onBackButtonClick = () => {
        if (invoke()) return;

        setDirection("backward");

        if (AssessmentUtils.isFirstSlideAndFirstGroup(settings)) {
            setIsCompleted(false);
        }

        setSettings(AssessmentUtils.moveToPreviousPage(settings));
    };

    return (
        <>
            {
                // if
                isCompleted && mskScore && (
                    <LinkLifestyleScreen
                        linkId={props.linkId}
                        screenSettings={settings}
                        setScreenSettings={setSettings}
                        settingsOnBack={onBackButtonClick}
                        user={props.user}
                        mskScore={mskScore}
                    />
                )
            }
            {
                // if
                !isCompleted && (
                    <BaseCardLayout>
                        <div className={`${COMPONENT_CLASS} -movement`}>
                            <LoaderAssessments isLoading={isLoading} loaderText={t('assessments.loadingAssessment')} />
                            {
                                // if
                                !isLoading && currentGroup != null && (
                                    <div
                                        className={`${COMPONENT_CLASS}__wrapper ${slide?.slideType === SlideType.GroupIntro
                                            ? "-intro"
                                            : ""
                                            }`}>

                                        {
                                            // if
                                            AssessmentUtils.hasData(screenSettings) &&
                                            movementScore == null && (
                                                <div
                                                    className={`${COMPONENT_CLASS}__content ${hasContinueButton ||
                                                        hasSubmitButton
                                                        ? "has-button"
                                                        : ""
                                                        }`}>
                                                    {
                                                        // if
                                                        slide != null && (
                                                            <ScreenSlide
                                                                isLinkAccountCreation={true}
                                                                questionGroupNames={
                                                                    questionGroupNames
                                                                }
                                                                recordedAnswers={AssessmentUtils.getAllResponses(
                                                                    screenSettings
                                                                )}
                                                                recordAnswer={recordAnswer}
                                                                page={slide}
                                                                errors={errors}
                                                                totalProgress={
                                                                    groups.length
                                                                }
                                                                currentProgress={
                                                                    groups.indexOf(
                                                                        currentGroup
                                                                    ) + 1
                                                                }
                                                                hasProgressBar={hasProgressBar}
                                                                screenSettings={screenSettings}
                                                                settingsOnBack={props.settingsOnBack}
                                                                isActionsWithContinue={hasContinueButton}
                                                                skipButton={slide?.questions &&
                                                                    slide.questions.length >
                                                                    0 &&
                                                                    slide.questions[0]
                                                                        .optional === true}
                                                                onSkip={getNextPage}
                                                                onBack={props.settingsOnBack}
                                                                onSubmit={getNextPage}
                                                                submitButtonText={continueButtonText}
                                                            />
                                                        )
                                                    }

                                                    <div
                                                        className={`${COMPONENT_CLASS}__actions ${actionsModifier} ion-margin-horizontal`}>
                                                        {/* {
                                            // if
                                            hasContinueButton && (
                                                <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={getNextPage} submitButtonText={continueButtonText}></BaseCardLayoutActions>
                                            )
                                        } */}
                                                        {/* {
                                                            // if
                                                            hasSubmitButtton && (
                                                                <div className={`c-screen-slide__actions`}>
                                                                    <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={submit} submitButtonText={errors.length === 0
                                                                        ? "Submit Results"
                                                                        : "Resubmit"}></BaseCardLayoutActions>
                                                                </div>
                                                                // <IonButton
                                                                //     color="primary"
                                                                //     expand="block"
                                                                //     onClick={() =>
                                                                //         submit()
                                                                //     }>
                                                                //     {errors.length === 0
                                                                //         ? "Submit Results"
                                                                //         : "Resubmit"}
                                                                // </IonButton>
                                                            )
                                                        } */}
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>
                    </BaseCardLayout>
                )
            }
        </>
    );
};

export default LinkMovementScreen;
